import React, { useState, useRef } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  PieChart, Pie, Cell
} from 'recharts';
import { regulatoryMatrix } from '../data/medical-device-database';
import Analytics from '../utils/analytics';

export default function ResultsDisplay({ 
  deviceInfo, 
  adminScore, 
  physicalScore, 
  technicalScore, 
  overallSafeguardsScore,
  threats,
  overallRiskScore,
  riskLevel,
  riskColor,
  medicalDevicesDatabase,
  selectedDevice,
  selectedDeviceCategoryId,
  onReset
}) {
  const [activeSection, setActiveSection] = useState('summary');
  const reportRef = useRef(null);
  
  // Format date for report
  const formattedDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Assessment completeness based on what's been filled in
  const safeguardsAnswered = [adminScore, physicalScore, technicalScore].filter(s => s > 0).length;
  const threatsAssessed = threats.filter(t => t.present || t.likelihood > 1 || t.severity > 1).length;
  const deviceComplete = !!(deviceInfo.deviceName && deviceInfo.manufacturer);
  const completenessScore = Math.round(
    (deviceComplete ? 20 : 0) +
    (safeguardsAnswered / 3) * 50 +
    (threatsAssessed / Math.max(threats.length, 1)) * 30
  );
  const completenessColor = completenessScore >= 80 ? 'text-green-600' : completenessScore >= 50 ? 'text-amber-600' : 'text-red-600';
  const completenessBg = completenessScore >= 80 ? 'bg-green-50 border-green-200' : completenessScore >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';

  // Create data for bar chart
  const safeguardsData = [
    { name: 'Administrative', score: parseFloat(adminScore.toFixed(1)) },
    { name: 'Physical', score: parseFloat(physicalScore.toFixed(1)) },
    { name: 'Technical', score: parseFloat(technicalScore.toFixed(1)) }
  ];
  
  // Create data for radar chart
  const radarData = threats
    .filter(threat => threat.present)
    .map(threat => ({
      subject: threat.name,
      score: threat.score,
      fullMark: 25,
    }));
  
  // Create data for pie chart
  const pieData = [
    { name: 'Risk Score', value: parseFloat(overallRiskScore) },
    { name: 'Remaining', value: 10 - parseFloat(overallRiskScore) }
  ];
  
  // HIPAA color mapping
  const COLORS = ['#FF8042', '#EEEEEE'];
  
  // Get compliance level based on overall safeguards score
  const getComplianceLevel = (score) => {
    if (score >= 8) return 'Substantial Compliance';
    if (score >= 6) return 'Partial Compliance';
    if (score >= 4) return 'Minimal Compliance';
    return 'Non-Compliant';
  };
  
  // Get associated HIPAA requirements for medical devices
  const getRelevantHipaaRequirements = () => {
    return regulatoryMatrix.hipaa.requirements.filter(req => 
      req.deviceRelevance.toLowerCase().includes('device') ||
      req.name.toLowerCase().includes('risk') ||
      req.name.toLowerCase().includes('security')
    );
  };
  
  // Generate PDF report using jsPDF + html2canvas
  const handleGeneratePDF = async () => {
    Analytics.trackEvent('Results', 'Generate PDF', 'Risk Assessment Report');
    const { default: jsPDF } = await import('jspdf');
    const { default: html2canvas } = await import('html2canvas');
    const element = reportRef.current;
    if (!element) { window.print(); return; }
    try {
      const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      const fileName = `${deviceInfo.deviceName || 'Device'}_Risk_Assessment_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
    } catch (err) {
      console.error('PDF generation failed, falling back to print', err);
      window.print();
    }
  };
  
  // Export CSV data
  const exportCsvData = () => {
    // Create CSV content
    let csvContent = "data:text/csv;charset=utf-8,";
    
    // Add headers
    csvContent += "Medical Device Risk Assessment Results\r\n";
    csvContent += `Device Name: ${deviceInfo.deviceName || 'Not specified'}\r\n`;
    csvContent += `Assessment Date: ${formattedDate}\r\n\r\n`;
    
    // Add safeguards scores
    csvContent += "Safeguards Scores\r\n";
    csvContent += "Category,Score\r\n";
    csvContent += `Administrative,${adminScore.toFixed(1)}\r\n`;
    csvContent += `Physical,${physicalScore.toFixed(1)}\r\n`;
    csvContent += `Technical,${technicalScore.toFixed(1)}\r\n`;
    csvContent += `Overall,${overallSafeguardsScore.toFixed(1)}\r\n\r\n`;
    
    // Add threat assessments
    csvContent += "Threat Assessments\r\n";
    csvContent += "Threat,Present,Severity,Likelihood,Score\r\n";
    threats.forEach(threat => {
      csvContent += `${threat.name},${threat.present},${threat.present ? threat.severity : 'N/A'},${threat.present ? threat.likelihood : 'N/A'},${threat.present ? threat.score : 'N/A'}\r\n`;
    });
    csvContent += `\r\nOverall Risk Score,${overallRiskScore}\r\n`;
    csvContent += `Risk Level,${riskLevel}\r\n`;
    
    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${deviceInfo.deviceName || 'Device'}_Risk_Assessment_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // --- Helper to get key regulatory requirements ---
  const getKeyRequirements = (matrix, regulationKey, requirementIds) => {
    if (!matrix || !matrix[regulationKey] || !matrix[regulationKey].requirements) {
      return [];
    }
    return matrix[regulationKey].requirements.filter(req => requirementIds.includes(req.id));
  };

  const hipaaKeyReqs = getKeyRequirements(medicalDevicesDatabase?.regulatoryMatrix, 'hipaa', ['h1', 'h2', 'h4', 'h7']);
  const fdaKeyReqs = getKeyRequirements(medicalDevicesDatabase?.regulatoryMatrix, 'fda', ['f1', 'f4']);

  // --- Helper to get relevant vulnerabilities ---
  const getRelevantVulnerabilities = () => {
    if (!selectedDeviceCategoryId || !medicalDevicesDatabase?.knownVulnerabilities?.categories) {
      return [];
    }
    const relevant = [];
    medicalDevicesDatabase.knownVulnerabilities.categories.forEach(category => {
      category.vulnerabilities.forEach(vuln => {
        if (vuln.affectedDeviceTypes?.includes(selectedDeviceCategoryId)) {
          relevant.push(vuln);
        }
      });
    });
    return relevant;
  };

  const relevantVulnerabilities = getRelevantVulnerabilities();

  return (
    <>
      {/* Simplified Print Styles - Always show detailed view */}
      <style>
        {`
          @media print {
            body {
              -webkit-print-color-adjust: exact; 
              print-color-adjust: exact; 
            }
            /* Hide UI elements not meant for the report */
            .print-hidden {
              display: none !important;
            }
            /* Ensure all report sections are visible when printing */
            .report-section {
               display: block !important; 
               page-break-inside: avoid; 
               margin-bottom: 1.5rem; /* Add spacing between sections */
            }
            /* Force page breaks before major sections */
             .report-section-safeguards,
             .report-section-threats,
             .report-section-regulatory {
                page-break-before: always;
             }
             /* Minor adjustments for print layout */
             .report-section-summary {
               margin-bottom: 2rem; /* More space after summary */
             }
             .chart-container-print {
                height: 200px !important; /* Slightly smaller charts for print */
             }
             .table {
               font-size: 9pt !important; /* Smaller font for tables */
             }
          }
        `}
      </style>

      {/* Main container - ref for potential future use, no format class needed */}
      <div className="bg-white p-6 rounded shadow mb-8 report-container" ref={reportRef}>
        {/* Header/Tabs (Hidden in Print) */}
        <div className="print-hidden">
          <h2 className="text-xl font-semibold mb-4">Assessment Results</h2>
          <div className="flex mb-6 border-b">
            <button 
              className={`py-2 px-4 mr-2 ${activeSection === 'summary' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
              onClick={() => setActiveSection('summary')}
            >
              Summary
            </button>
            <button 
              className={`py-2 px-4 mr-2 ${activeSection === 'safeguards' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
              onClick={() => setActiveSection('safeguards')}
            >
              Safeguards
            </button>
            <button 
              className={`py-2 px-4 mr-2 ${activeSection === 'threats' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
              onClick={() => setActiveSection('threats')}
            >
              Threats
            </button>
            <button 
              className={`py-2 px-4 ${activeSection === 'compliance' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
              onClick={() => setActiveSection('compliance')}
            >
              Compliance
            </button>
          </div>
        </div>
        
        {/* Summary Section - Conditionally hidden on screen, always visible in print */}
        <div className={`report-section report-section-summary mb-6 ${activeSection !== 'summary' && !window.matchMedia('print').matches ? 'hidden' : ''}`}>
          <div className="text-center mb-4 print:mb-8">
            <h1 className="text-2xl font-bold print:text-3xl">Medical Device Security Risk Assessment</h1>
            <p className="text-gray-500 print:text-lg">{formattedDate}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print:gap-4">
            <div className="bg-gray-50 p-4 rounded print:border print:bg-white">
              <h3 className="font-medium text-lg mb-2">Device Information</h3>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 font-medium">Device Name:</td>
                    <td className="py-2">{deviceInfo.deviceName || 'Not specified'}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-medium">Manufacturer:</td>
                    <td className="py-2">{deviceInfo.manufacturer || 'Not specified'}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-medium">Model:</td>
                    <td className="py-2">{deviceInfo.model || 'Not specified'}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-medium">Software Version:</td>
                    <td className="py-2">{deviceInfo.softwareVersion || 'Not specified'}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-medium">Network Connected:</td>
                    <td className="py-2">{deviceInfo.networkConnected ? 'Yes' : 'No'}</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium">Contains ePHI:</td>
                    <td className="py-2">{deviceInfo.containsEphi ? 'Yes' : 'No'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="bg-gray-50 p-4 rounded flex flex-col print:border print:bg-white">
              <h3 className="font-medium text-lg mb-2">Risk Assessment</h3>
              <div className="flex-grow flex flex-col items-center justify-center">
                <div className="flex items-center mb-4">
                  <div 
                    className="rounded-full w-24 h-24 flex items-center justify-center text-white text-3xl font-bold print:border-4 print:border-gray-400"
                    style={{ backgroundColor: riskColor }}
                  >
                    {overallRiskScore}
                  </div>
                  <div className="ml-6">
                    <p className="text-lg font-semibold print:text-xl" style={{ color: riskColor }}>
                      {riskLevel} Risk
                    </p>
                    <p className="text-sm text-gray-600 mt-1">Overall Risk Score</p>
                    
                    <div className="mt-3 text-sm">
                      <div className="flex items-center">
                        <span className="font-medium mr-2">Safeguards Score:</span>
                        <span>{overallSafeguardsScore.toFixed(1)}/10</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium mr-2">Compliance Level:</span>
                        <span>{getComplianceLevel(overallSafeguardsScore)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Assessment Completeness */}
              <div className={`mt-4 p-3 rounded-lg border ${completenessBg}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-medium ${completenessColor}`}>Assessment Completeness</span>
                  <span className={`text-lg font-bold ${completenessColor}`}>{completenessScore}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${completenessScore >= 80 ? 'bg-green-500' : completenessScore >= 50 ? 'bg-amber-400' : 'bg-red-500'}`}
                    style={{ width: `${completenessScore}%` }}
                  />
                </div>
                {completenessScore < 100 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {!deviceComplete && 'Add device name & manufacturer. '}
                    {safeguardsAnswered < 3 && 'Complete all safeguard sections. '}
                    {threatsAssessed < threats.length && 'Review threat assessments.'}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded print:mt-8 print:border print:bg-white">
            <h3 className="font-medium text-lg mb-2">Required Actions</h3>
            <div className="mb-2">
              {riskLevel === 'Critical' && (
                <div className="flex items-start print:mb-4">
                  <div className="bg-red-100 text-red-800 p-1 rounded-full text-xs font-bold mr-2 mt-0.5">!</div>
                  <div>
                    <p className="font-medium text-red-800">Immediate remediation required (within 7 days)</p>
                    <p className="text-sm">Implement emergency controls and consider restricting device use until risks are mitigated. Document all actions taken.</p>
                    <p className="text-sm font-medium mt-2">Priority threats to address:</p>
                    <ul className="list-disc ml-5 text-sm">
                      {threats
                        .filter(t => t.present)
                        .sort((a, b) => b.score - a.score)
                        .slice(0, 3)
                        .map((threat, idx) => (
                          <li key={idx}>{threat.name} (Score: {threat.score})</li>
                        ))
                      }
                    </ul>
                  </div>
                </div>
              )}
              {riskLevel === 'High' && (
                <div className="flex items-start print:mb-4">
                  <div className="bg-yellow-100 text-yellow-800 p-1 rounded-full text-xs font-bold mr-2 mt-0.5">!</div>
                  <div>
                    <p className="font-medium text-yellow-800">Remediation required within 30 days</p>
                    <p className="text-sm">Develop and implement a risk treatment plan with clear milestones and responsibilities. Focus on addressing highest scoring threats first.</p>
                    <p className="text-sm font-medium mt-2">Priority threats to address:</p>
                    <ul className="list-disc ml-5 text-sm">
                      {threats
                        .filter(t => t.present)
                        .sort((a, b) => b.score - a.score)
                        .slice(0, 3)
                        .map((threat, idx) => (
                          <li key={idx}>{threat.name} (Score: {threat.score})</li>
                        ))
                      }
                    </ul>
                  </div>
                </div>
              )}
              {riskLevel === 'Medium' && (
                <div className="flex items-start print:mb-4">
                  <div className="bg-orange-100 text-orange-800 p-1 rounded-full text-xs font-bold mr-2 mt-0.5">!</div>
                  <div>
                    <p className="font-medium text-orange-800">Remediation recommended within 90 days</p>
                    <p className="text-sm">Document risks and implement compensating controls where feasible. Include in your organization's risk management plan.</p>
                    <p className="text-sm font-medium mt-2">Key safeguard improvements:</p>
                    <ul className="list-disc ml-5 text-sm">
                      {adminScore < 6 && <li>Strengthen administrative safeguards: policies, procedures, and training</li>}
                      {physicalScore < 6 && <li>Enhance physical safeguards: access controls and device management</li>}
                      {technicalScore < 6 && <li>Improve technical safeguards: authentication, encryption, and audit controls</li>}
                    </ul>
                  </div>
                </div>
              )}
              {riskLevel === 'Low' && (
                <div className="flex items-start print:mb-4">
                  <div className="bg-green-100 text-green-800 p-1 rounded-full text-xs font-bold mr-2 mt-0.5">✓</div>
                  <div>
                    <p className="font-medium text-green-800">Monitor and address in annual security review</p>
                    <p className="text-sm">Ensure regular updates and staff training are maintained. Document acceptance of remaining low-level risks.</p>
                    <p className="text-sm font-medium mt-2">Recommended maintenance:</p>
                    <ul className="list-disc ml-5 text-sm">
                      <li>Continue regular security updates and patches</li>
                      <li>Maintain staff awareness training</li>
                      <li>Review and update risk assessment at least annually</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Safeguards Section - Conditionally hidden on screen, always visible in print */}
        <div className={`report-section report-section-safeguards mb-6 ${activeSection !== 'safeguards' && !window.matchMedia('print').matches ? 'hidden' : ''}`}>
          <h3 className="font-medium text-lg mb-4">Safeguards Assessment</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
             {/* Safeguards Table */}
             <div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left p-2 border">Safeguard Category</th>
                      <th className="text-center p-2 border">Score</th>
                      <th className="text-center p-2 border">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-2 border">Administrative Safeguards</td>
                      <td className="p-2 border text-center">{adminScore.toFixed(1)}/10</td>
                      <td className="p-2 border text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          adminScore >= 8 ? 'bg-green-100 text-green-800' : 
                          adminScore >= 6 ? 'bg-blue-100 text-blue-800' : 
                          adminScore >= 4 ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-red-100 text-red-800'
                        }`}>
                          {adminScore >= 8 ? 'Strong' : 
                           adminScore >= 6 ? 'Adequate' : 
                           adminScore >= 4 ? 'Needs Improvement' : 
                           'Deficient'}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-2 border">Physical Safeguards</td>
                      <td className="p-2 border text-center">{physicalScore.toFixed(1)}/10</td>
                      <td className="p-2 border text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          physicalScore >= 8 ? 'bg-green-100 text-green-800' : 
                          physicalScore >= 6 ? 'bg-blue-100 text-blue-800' : 
                          physicalScore >= 4 ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-red-100 text-red-800'
                        }`}>
                          {physicalScore >= 8 ? 'Strong' : 
                           physicalScore >= 6 ? 'Adequate' : 
                           physicalScore >= 4 ? 'Needs Improvement' : 
                           'Deficient'}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-2 border">Technical Safeguards</td>
                      <td className="p-2 border text-center">{technicalScore.toFixed(1)}/10</td>
                      <td className="p-2 border text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          technicalScore >= 8 ? 'bg-green-100 text-green-800' : 
                          technicalScore >= 6 ? 'bg-blue-100 text-blue-800' : 
                          technicalScore >= 4 ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-red-100 text-red-800'
                        }`}>
                          {technicalScore >= 8 ? 'Strong' : 
                           technicalScore >= 6 ? 'Adequate' : 
                           technicalScore >= 4 ? 'Needs Improvement' : 
                           'Deficient'}
                        </span>
                      </td>
                    </tr>
                    <tr className="font-medium">
                      <td className="p-2 border">Overall Safeguards</td>
                      <td className="p-2 border text-center">{overallSafeguardsScore.toFixed(1)}/10</td>
                      <td className="p-2 border text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          overallSafeguardsScore >= 8 ? 'bg-green-100 text-green-800' : 
                          overallSafeguardsScore >= 6 ? 'bg-blue-100 text-blue-800' : 
                          overallSafeguardsScore >= 4 ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-red-100 text-red-800'
                        }`}>
                          {overallSafeguardsScore >= 8 ? 'Strong' : 
                           overallSafeguardsScore >= 6 ? 'Adequate' : 
                           overallSafeguardsScore >= 4 ? 'Needs Improvement' : 
                           'Deficient'}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
             </div>
             {/* Safeguards Bar Chart */}
             <div className="h-64 chart-container-print">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={safeguardsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 10]} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="score" fill="#4299e1" />
                  </BarChart>
                </ResponsiveContainer>
             </div>
          </div>
          {/* Recommendations */} 
          <div className="bg-gray-50 p-4 rounded print:border print:bg-white">
            <h4 className="font-medium mb-2">Top Safeguard Recommendations</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border rounded p-3 bg-white">
                <h5 className="font-medium text-sm mb-1">Administrative</h5>
                <ul className="list-disc ml-4 text-xs space-y-1">
                  {adminScore < 8 && (
                    <>
                      <li>Develop and document comprehensive security policies specific to this device</li>
                      <li>Implement regular security awareness training for all staff with access</li>
                      <li>Establish and test contingency plans for device failure or security incidents</li>
                    </>
                  )}
                  {adminScore >= 8 && (
                    <li>Maintain current administrative safeguards and review regularly</li>
                  )}
                </ul>
              </div>
              
              <div className="border rounded p-3 bg-white">
                <h5 className="font-medium text-sm mb-1">Physical</h5>
                <ul className="list-disc ml-4 text-xs space-y-1">
                  {physicalScore < 8 && (
                    <>
                      <li>Implement stronger physical access controls to device and facilities</li>
                      <li>Develop and enforce media handling procedures for device data</li>
                      <li>Document and test workstation security procedures</li>
                    </>
                  )}
                  {physicalScore >= 8 && (
                    <li>Maintain current physical safeguards and audit regularly</li>
                  )}
                </ul>
              </div>
              
              <div className="border rounded p-3 bg-white">
                <h5 className="font-medium text-sm mb-1">Technical</h5>
                <ul className="list-disc ml-4 text-xs space-y-1">
                  {technicalScore < 8 && (
                    <>
                      <li>Implement strong authentication controls for device access</li>
                      <li>Enable and monitor audit logs for all device activity</li>
                      <li>Deploy encryption for data at rest and in transit</li>
                    </>
                  )}
                  {technicalScore >= 8 && (
                    <li>Maintain current technical safeguards and test regularly</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        {/* Threats Section - Conditionally hidden on screen, always visible in print */}
        <div className={`report-section report-section-threats mb-6 ${activeSection !== 'threats' && !window.matchMedia('print').matches ? 'hidden' : ''}`}>
            <h3 className="font-medium text-lg mb-4">Threat Assessment</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Threat Table */} 
                <div>
                   <table className="w-full text-sm">
                     <thead>
                       <tr className="bg-gray-100">
                         <th className="text-left p-2 border">Threat</th>
                         <th className="text-center p-2 border">Present</th>
                         <th className="text-center p-2 border">Severity</th>
                         <th className="text-center p-2 border">Likelihood</th>
                         <th className="text-center p-2 border">Score</th>
                       </tr>
                     </thead>
                     <tbody>
                       {threats.map((threat, index) => (
                         <tr key={index}>
                           <td className="p-2 border">{threat.name}</td>
                           <td className="p-2 border text-center">{threat.present ? 'Yes' : 'No'}</td>
                           <td className="p-2 border text-center">{threat.present ? threat.severity : '-'}</td>
                           <td className="p-2 border text-center">{threat.present ? threat.likelihood : '-'}</td>
                           <td className="p-2 border text-center font-medium">
                             {threat.present ? (
                               <span className={`px-2 py-1 rounded ${
                                 threat.score >= 16 ? 'bg-red-100 text-red-800' : 
                                 threat.score >= 9 ? 'bg-orange-100 text-orange-800' : 
                                 'bg-yellow-100 text-yellow-800'
                               }`}>
                                 {threat.score}
                               </span>
                             ) : '-'}
                           </td>
                         </tr>
                       ))}
                     </tbody>
                   </table>
                </div>
                {/* Threat Radar Chart */} 
                <div className="h-64 chart-container-print">
                   {radarData.length > 0 ? (
                     <ResponsiveContainer width="100%" height="100%">
                       <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                         <PolarGrid />
                         <PolarAngleAxis dataKey="subject" />
                         <PolarRadiusAxis angle={30} domain={[0, 25]} />
                         <Radar 
                           name="Threat Score" 
                           dataKey="score" 
                           stroke="#FF8042" 
                           fill="#FF8042" 
                           fillOpacity={0.6} 
                         />
                         <Tooltip />
                         <Legend />
                       </RadarChart>
                     </ResponsiveContainer>
                   ) : (
                     <div className="flex h-full w-full items-center justify-center bg-gray-50 border rounded">
                       <p className="text-gray-500">No threats marked as present</p>
                     </div>
                   )}
                </div>
            </div>
             {/* Top Threats List */} 
             {radarData.length > 0 && (
               <div className="bg-gray-50 p-4 rounded print:border print:bg-white">
                   <h4 className="font-medium mb-2">Top Threats by Risk Score</h4>
                   <div className="space-y-3">
                     {threats
                       .filter(t => t.present)
                       .sort((a, b) => b.score - a.score)
                       .slice(0, 3)
                       .map((threat, idx) => (
                         <div key={idx} className="border rounded p-3 bg-white">
                           <div className="flex justify-between items-center">
                             <h5 className="font-medium">{threat.name}</h5>
                             <span className={`px-2 py-1 rounded text-xs ${
                               threat.score >= 16 ? 'bg-red-100 text-red-800' : 
                               threat.score >= 9 ? 'bg-orange-100 text-orange-800' : 
                               'bg-yellow-100 text-yellow-800'
                             }`}>
                               Score: {threat.score}
                             </span>
                           </div>
                           <p className="text-xs mt-1">{threat.description}</p>
                           
                           <div className="mt-2 text-xs">
                             <p className="font-medium">Recommended Mitigations:</p>
                             <ul className="list-disc ml-4 mt-1 space-y-1">
                               {threat.name.toLowerCase().includes('spoofing') && (
                                 <>
                                   <li>Implement multi-factor authentication</li>
                                   <li>Use strong credential management</li>
                                   <li>Apply principle of least privilege</li>
                                 </>
                               )}
                               {threat.name.toLowerCase().includes('tampering') && (
                                 <>
                                   <li>Implement integrity verification mechanisms</li>
                                   <li>Apply access controls to prevent unauthorized modifications</li>
                                   <li>Use digital signatures for critical data</li>
                                 </>
                               )}
                               {threat.name.toLowerCase().includes('repudiation') && (
                                 <>
                                   <li>Implement comprehensive audit logging</li>
                                   <li>Use secure timestamps for all activities</li>
                                   <li>Maintain tamper-proof logs</li>
                                 </>
                               )}
                               {threat.name.toLowerCase().includes('information disclosure') && (
                                 <>
                                   <li>Implement data encryption for storage and transmission</li>
                                   <li>Apply strict access controls</li>
                                   <li>Use secure data disposal procedures</li>
                                 </>
                               )}
                               {threat.name.toLowerCase().includes('denial of service') && (
                                 <>
                                   <li>Implement redundancy and failover capabilities</li>
                                   <li>Use resource throttling and monitoring</li>
                                   <li>Develop and test contingency plans</li>
                                 </>
                               )}
                               {threat.name.toLowerCase().includes('elevation of privilege') && (
                                 <>
                                   <li>Implement principle of least privilege</li>
                                   <li>Use proper input validation and sanitization</li>
                                   <li>Regularly audit permissions and access rights</li>
                                 </>
                               )}
                             </ul>
                           </div>
                         </div>
                       ))
                     }
                   </div>
               </div>
              )}
        </div>
        
        {/* Regulatory & Vulnerabilities Section - Conditionally hidden on screen, always visible in print */}
        <div className={`report-section report-section-regulatory mb-6 ${activeSection !== 'compliance' && !window.matchMedia('print').matches ? 'hidden' : ''}`}>
          <h3 className="font-medium text-lg mb-4">Regulatory Considerations & Vulnerabilities</h3>
          
          {/* Relevant Regulatory Considerations Sub-Section */}
          <section className="bg-white p-4 mb-6 print:p-0">
            <h2 className="text-xl font-semibold mb-4 border-b pb-2">Relevant Regulatory Considerations</h2>
            <p className="text-sm text-gray-600 mb-4">
              Below are some key requirements from relevant regulations that often apply to medical device security. 
              Refer to the full regulations for complete details.
            </p>
            
            <div className="mb-4">
              <h3 className="font-medium text-lg mb-2">HIPAA/HITECH Highlights</h3>
              {hipaaKeyReqs.length > 0 ? (
                <ul className="list-disc ml-5 text-sm space-y-2">
                  {hipaaKeyReqs.map(req => (
                    <li key={req.id}>
                      <strong className="font-semibold">{req.name} ({req.citation}):</strong> {req.description}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm italic text-gray-500">HIPAA requirements data not available.</p>
              )}
            </div>

            <div>
              <h3 className="font-medium text-lg mb-2">FDA Guidance Highlights</h3>
              {fdaKeyReqs.length > 0 ? (
                 <ul className="list-disc ml-5 text-sm space-y-2">
                  {fdaKeyReqs.map(req => (
                    <li key={req.id}>
                      <strong className="font-semibold">{req.name} ({req.citation}):</strong> {req.description}
                    </li>
                  ))}
                </ul>
              ) : (
                 <p className="text-sm italic text-gray-500">FDA requirements data not available.</p>
              )}
            </div>
          </section>

          {/* Common Vulnerabilities Sub-Section */} 
          <section className="bg-white p-4 print:p-0">
             <h2 className="text-xl font-semibold mb-4 border-b pb-2">Common Vulnerabilities for Device Type</h2>
             {selectedDevice ? (
               <> 
                 {relevantVulnerabilities.length > 0 ? (
                   <> 
                     <p className="text-sm text-gray-600 mb-4">
                       The following are common vulnerabilities associated with the <strong className="font-semibold">{selectedDevice.name}</strong> category based on our database. 
                       Review these in the context of your specific device and implementation.
                     </p>
                     <div className="space-y-4">
                       {relevantVulnerabilities.map(vuln => (
                         <div key={vuln.id} className="p-3 border rounded bg-orange-50">
                           <h4 className="font-medium mb-1">{vuln.name}</h4>
                           <p className="text-xs text-gray-700 mb-1">{vuln.description}</p>
                           <p className="text-xs"><strong className="font-semibold">Mitigation Example:</strong> {vuln.mitigation}</p>
                           {vuln.cve && <p className="text-xs mt-1 text-gray-500">Reference: {vuln.cve}</p>}
                         </div>
                       ))}
                     </div>
                   </>
                 ) : (
                   <p className="text-sm italic text-gray-500">
                     No specific common vulnerabilities listed in the database for the selected device category ('{selectedDeviceCategoryId}'). 
                     General security best practices should still be applied.
                   </p>
                 )}
               </>
             ) : (
               <p className="text-sm italic text-gray-500">
                 Common vulnerability information is based on device types from our database. This section is not applicable for custom device entries.
               </p>
             )}
          </section>
        </div>

        {/* Export buttons section - Keep this near the end, before the sponsors section */}
        <div className="mt-6 print-hidden">
          <h3 className="font-medium text-lg mb-2">Export/Print Results</h3>
          <div className="flex flex-wrap gap-2">
            <button 
              onClick={handleGeneratePDF}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Print/Save as PDF
            </button>
            <button 
              onClick={exportCsvData}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Export CSV Data
            </button>
            <button 
              onClick={onReset}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Reset Assessment
            </button>
          </div>
        </div>

        {/* Footer for printed reports */}
        <div className="mt-6 text-center text-xs text-gray-500 print:mt-8">
          <p>Generated by HIPAA/HITECH Medical Device Risk Assessment Tool on {formattedDate}</p>
          <p className="mt-1">This report is for informational purposes and does not guarantee HIPAA compliance.</p>
        </div>

      </div>
    </>
  );
}